import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  CButton,
  CCard,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CLoadingButton,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import ToastComponent from 'src/components/common/TaostComponent'
import GradeService from 'src/service/GradeService'
import Notify from '../Notify'
import MultiplierModal from './MultiplierModal'
import CopyGradeModal from './CopyGradeModal'
import { positionCategoryMap, allMappedKeys, SR_KEYS, ECON_KEYS } from './positionCategoryMap'

// SR/Econ rows are hidden (not just greyed out) when disabled, since visibility for
// these is driven by the "Set SR & Econ" setting rather than manual per-row toggling
const SR_ECON_KEYS = new Set([...SR_KEYS, ...ECON_KEYS])

// 7 categories shown in the unified dropdown, in display order
const UNIFIED_CATEGORIES = Object.keys(positionCategoryMap.ar)

// AttributeCell and CategoryCard MUST stay at module scope. Declaring a component inside
// PointSystem gives it a fresh identity on every render, so React unmounts and remounts the
// whole subtree on each keystroke - which resets these uncontrolled (defaultValue) inputs and
// makes the point fields impossible to type into.

// Reusable attribute name + dedicated toggle column (renders two <CTableDataCell>s)
const AttributeCell = ({ item, category, isActive, onToggle }) => (
  <React.Fragment>
    <CTableDataCell style={{ opacity: isActive ? 1 : 0.45, verticalAlign: 'middle' }}>
      {item.attribute_name}
    </CTableDataCell>
    <CTableDataCell style={{ verticalAlign: 'middle', width: 96 }}>
      <CFormSwitch
        className="ps-switch"
        size="lg"
        label={isActive ? 'On' : 'Off'}
        checked={isActive}
        onChange={() => onToggle(item, category)}
        title={isActive ? `Disable ${item.attribute_name}` : `Enable ${item.attribute_name}`}
      />
    </CTableDataCell>
  </React.Fragment>
)

// One category (Batting, Bowling, ...) as its own rounded/bordered card. The toggle-all
// switch is a row inside the table itself (not a separate header bar) so it lands in the
// exact same column as each item's own switch below it, instead of floating at the far edge.
const CategoryCard = ({
  category,
  rows,
  headerCells,
  valueColumnCount,
  isActive,
  onToggle,
  children,
}) => (
  <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
    <CTable align="middle" className="mb-0 ps-item-table">
      <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
        <CTableRow>
          <CTableDataCell className="fw-semibold" style={{ border: 0 }}>
            {category}
          </CTableDataCell>
          <CTableDataCell style={{ width: 96, border: 0 }}>
            <CFormSwitch
              className="ps-switch"
              size="lg"
              label={isActive ? 'On' : 'Off'}
              checked={isActive}
              onChange={() => onToggle(category, rows)}
              title={isActive ? `Disable all ${category}` : `Enable all ${category}`}
            />
          </CTableDataCell>
          {valueColumnCount > 0 && (
            <CTableDataCell colSpan={valueColumnCount} style={{ border: 0 }}></CTableDataCell>
          )}
        </CTableRow>
        <CTableRow>
          <CTableHeaderCell>Items</CTableHeaderCell>
          <CTableHeaderCell style={{ width: 96 }}></CTableHeaderCell>
          {headerCells}
        </CTableRow>
      </CTableHead>
      <CTableBody>{children}</CTableBody>
    </CTable>
  </CCard>
)

const PointSystem = (props) => {
  const [loader, setLoader] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [handleNo, setHandleNo] = useState(false)
  const [handleYes, setHandleYes] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [positionFilterCategory, setPositionFilterCategory] = useState('All')
  const [activeMap, setActiveMap] = useState({})

  // Initialise / sync activeMap whenever data loads
  useEffect(() => {
    if (!props.gradePointData) return
    const map = {}
    props.gradePointData.forEach((item) => {
      map[item.id] = item.is_active !== 0
    })
    setActiveMap(map)
  }, [props.gradePointData])

  const formik = useFormik({
    initialValues: {
      data: props.gradePointData,
    },
    enableReinitialize: true,
    onSubmit: (data, values) => {
      data.fixtureId = props.gradeId
      setLoader(true)
      GradeService.updateGradePointSystem(data)
        .then((res) => {
          if (res.status === 200) {
            GradeService.gradePointSystem(props.gradeId).then((res) => {
              props.setGradePointData(res.data)
              props.toggleDetails(props?.gradeId)
              props.fetchUsers()
            })
            setLoader(false)
            ToastComponent(res.message, 'success')
          }
        })
        .catch((e) => {
          setLoader(false)
          ToastComponent(e.response?.data?.message, 'error')
        })
    },
  })

  const handleConfirm = () => {
    setLoader(true)
    GradeService.resetToDefault(props.gradeId)
      .then((res) => {
        if (res.status === 200) {
          GradeService.gradePointSystem(props.gradeId).then((res) => {
            props.toggleDetails(props?.gradeId)
            props.fetchUsers()
            setLoader(false)
          })
          ToastComponent(res.message, 'success')
        }
      })
      .catch((e) => {
        ToastComponent('Something went wrong.Please try again.', 'error')
      })
    return setShowConfirm(false)
  }

  const handleCancel = () => {
    return setShowConfirm(false)
  }

  const handleReset = (resetForm) => {
    if (window.confirm('Reset?')) {
      resetForm()
    }
  }

  const [visible, setVisible] = useState(false)
  const [copyVisible, setCopyVisible] = useState(false)
  const [gradeList, setGradeList] = useState()

  const handleCopyVisible = () => {
    setCopyVisible(true)
    GradeService.gradeData()
      .then((res) => {
        if (res.status === 200) {
          setLoader(false)
          setGradeList(res.data)
        }
      })
      .catch((e) => {
        ToastComponent(e.response?.data?.message, 'error')
        setLoader(false)
        ToastComponent(e.response?.data?.message, 'error')
      })
  }

  // Warn once per data load for attribute_keys not mapped to any position
  useEffect(() => {
    if (!props.gradePointData) return
    props.gradePointData.forEach((item) => {
      if (!allMappedKeys.has(item.attribute_key)) {
        console.warn(
          `[PointSystem] attribute_key "${item.attribute_key}" (${item.attribute_name}) is not mapped to any position — excluded from grouped view`,
        )
      }
    })
  }, [props.gradePointData])

  // A row is hidden when it's an SR/Econ attribute that's currently disabled — visibility
  // for those is driven by the "Set SR & Econ" setting, not manual grey-out
  const isRowVisible = useCallback(
    ({ item }) => !(SR_ECON_KEYS.has(item.attribute_key) && activeMap[item.id] === false),
    [activeMap],
  )

  // Position-based: all rows grouped by category (uses ar map = full union)
  const groupedRows = useMemo(() => {
    if (!props.gradePointData) return []
    const dataByKey = {}
    props.gradePointData.forEach((item, originalIndex) => {
      dataByKey[item.attribute_key] = { item, originalIndex }
    })
    return Object.entries(positionCategoryMap.ar)
      .map(([category, keys]) => ({
        category,
        rows: keys.filter((key) => dataByKey[key]).map((key) => dataByKey[key]),
      }))
      .map(({ category, rows }) => ({ category, rows: rows.filter(isRowVisible) }))
      .filter(({ rows }) => rows.length > 0)
  }, [props.gradePointData, isRowVisible])

  // Unified: only the rows belonging to the selected category
  const unifiedCategoryRows = useMemo(() => {
    if (!props.gradePointData) return []
    const keys = positionCategoryMap.ar[selectedCategory] || []
    const dataByKey = {}
    props.gradePointData.forEach((item, originalIndex) => {
      dataByKey[item.attribute_key] = { item, originalIndex }
    })
    return keys
      .filter((key) => dataByKey[key])
      .map((key) => dataByKey[key])
      .filter(isRowVisible)
  }, [props.gradePointData, selectedCategory, isRowVisible])

  // Unified input handler: one value → copied to all 4 positions
  const handleUnifiedChange = (originalIndex, value) => {
    formik.setFieldValue(`data.${originalIndex}.bats`, value)
    formik.setFieldValue(`data.${originalIndex}.bowler`, value)
    formik.setFieldValue(`data.${originalIndex}.wk`, value)
    formik.setFieldValue(`data.${originalIndex}.ar`, value)
  }

  // ── Toggle helpers ──

  const isCategoryActive = (rows) =>
    rows.length > 0 && rows.every(({ item }) => activeMap[item.id] !== false)

  const handleItemToggle = (item, category) => {
    const newActive = !activeMap[item.id]
    const prevMap = activeMap
    setActiveMap({ ...activeMap, [item.id]: newActive })
    GradeService.toggleGamePoint({
      id: item.id,
      is_active: newActive ? 1 : 0,
      attribute_name: item.attribute_name,
      category,
    })
      .then((res) => {
        if (res.status === 200) ToastComponent(res.message, 'success')
      })
      .catch((e) => {
        setActiveMap(prevMap)
        ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
      })
  }

  const handleCategoryToggle = (category, rows) => {
    const newActive = !isCategoryActive(rows)
    const prevMap = activeMap
    const updatedMap = { ...activeMap }
    const attributeKeys = []
    rows.forEach(({ item }) => {
      updatedMap[item.id] = newActive
      attributeKeys.push(item.attribute_key)
    })
    setActiveMap(updatedMap)
    GradeService.toggleGamePointCategory({
      grade_id: props.gradeId,
      attribute_keys: attributeKeys,
      is_active: newActive ? 1 : 0,
      category,
    })
      .then((res) => {
        if (res.status === 200) ToastComponent(res.message, 'success')
      })
      .catch((e) => {
        setActiveMap(prevMap)
        ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
      })
  }

  // Position-free games always use the single-column layout; label differs
  const isPositionFree = props.teamView === 1
  const isUnified = isPositionFree || props.gamePointSystemType === 1
  const singleColHeader = isPositionFree ? 'Points' : 'Bat / Bowl / AR / WK'

  return (
    <>
      <style>{`
        .ps-switch.form-check {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
        }
        .ps-switch .form-check-input {
          cursor: pointer;
        }
        .ps-switch .form-check-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--cui-secondary-color);
          user-select: none;
        }
        .ps-switch.form-check .form-check-input:not(:checked) {
          background-color: #dc3545;
          border-color: #dc3545;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
        }
        .ps-item-table > :not(caption) > * > * {
          padding-top: .75rem;
          padding-bottom: .75rem;
        }
        .ps-item-table thead th {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
      `}</style>
      <CForm className="row g-3" onSubmit={formik.handleSubmit}>
        {/* ── UNIFIED VIEW (game_point_system_type === 2 OR position-free) ── */}
        {isUnified && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <CFormSelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ minWidth: '160px', width: 'auto', display: 'inline-block' }}
              >
                <option value="All">All</option>
                {UNIFIED_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </CFormSelect>
            </div>

            {(selectedCategory === 'All'
              ? groupedRows
              : [{ category: selectedCategory, rows: unifiedCategoryRows }]
            ).map(({ category, rows }) => (
              <CategoryCard
                key={category}
                category={category}
                rows={rows}
                valueColumnCount={1}
                isActive={isCategoryActive(rows)}
                onToggle={handleCategoryToggle}
                headerCells={<CTableHeaderCell>{singleColHeader}</CTableHeaderCell>}
              >
                {rows.map(({ item, originalIndex }) => (
                  <CTableRow key={originalIndex}>
                    <AttributeCell
                      item={item}
                      category={category}
                      isActive={activeMap[item.id] !== false}
                      onToggle={handleItemToggle}
                    />
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        inputMode="decimal"
                        size="sm"
                        defaultValue={item.bats}
                        onChange={(e) => handleUnifiedChange(originalIndex, e.target.value)}
                        disabled={activeMap[item.id] === false}
                      />
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {rows.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={3}>No record yet available.</CTableDataCell>
                  </CTableRow>
                )}
              </CategoryCard>
            ))}

            {(selectedCategory === 'All' ? groupedRows : unifiedCategoryRows).length === 0 && (
              <p style={{ padding: '12px' }}>No record yet available.</p>
            )}
          </>
        )}

        {/* ── POSITION-BASED VIEW (game_point_system_type === 1) ── */}
        {!isUnified && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <CFormSelect
                value={positionFilterCategory}
                onChange={(e) => setPositionFilterCategory(e.target.value)}
                style={{ minWidth: '160px', width: 'auto', display: 'inline-block' }}
              >
                <option value="All">All</option>
                {UNIFIED_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </CFormSelect>
            </div>

            {(positionFilterCategory === 'All'
              ? groupedRows
              : groupedRows.filter(({ category }) => category === positionFilterCategory)
            ).map(({ category, rows }) => (
              <CategoryCard
                key={category}
                category={category}
                rows={rows}
                valueColumnCount={4}
                isActive={isCategoryActive(rows)}
                onToggle={handleCategoryToggle}
                headerCells={
                  <>
                    <CTableHeaderCell>Bowler</CTableHeaderCell>
                    <CTableHeaderCell>Batsman</CTableHeaderCell>
                    <CTableHeaderCell>Wicket Keeper</CTableHeaderCell>
                    <CTableHeaderCell>All Rounder</CTableHeaderCell>
                  </>
                }
              >
                {rows.map(({ item, originalIndex }) => (
                  <CTableRow key={originalIndex}>
                    <AttributeCell
                      item={item}
                      category={category}
                      isActive={activeMap[item.id] !== false}
                      onToggle={handleItemToggle}
                    />
                    <CTableDataCell>
                      <CFormInput
                        name={`data.${originalIndex}.bowler`}
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        defaultValue={item.bowler}
                        onChange={formik.handleChange}
                        disabled={activeMap[item.id] === false}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        name={`data.${originalIndex}.bats`}
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        defaultValue={item.bats}
                        onChange={formik.handleChange}
                        disabled={activeMap[item.id] === false}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        name={`data.${originalIndex}.wk`}
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        defaultValue={item.wk}
                        onChange={formik.handleChange}
                        disabled={activeMap[item.id] === false}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        name={`data.${originalIndex}.ar`}
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        defaultValue={item.ar}
                        onChange={formik.handleChange}
                        disabled={activeMap[item.id] === false}
                      />
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {rows.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={6}>No record yet available.</CTableDataCell>
                  </CTableRow>
                )}
              </CategoryCard>
            ))}

            {(positionFilterCategory === 'All'
              ? groupedRows
              : groupedRows.filter(({ category }) => category === positionFilterCategory)
            ).length === 0 && <p style={{ padding: '12px' }}>No record yet available.</p>}
          </>
        )}

        <CRow>
          <CCol xs={12}>
            {props.fixtureExists === 0 && (
              <>
                <CButton
                  type="button"
                  className="mb-3 mx-1"
                  color={'light'}
                  onClick={() => {
                    props.toggleDetails(props.gradeId)
                  }}
                >
                  {'Cancel'}
                </CButton>
                <CButton
                  type="button"
                  className="mb-3 mx-1"
                  color={'warning'}
                  onClick={handleReset.bind(null, formik.resetForm)}
                >
                  {'Clear'}
                </CButton>
                <CButton
                  type="button"
                  className="mb-3 mx-1"
                  color={'danger'}
                  onClick={() => setShowConfirm(true)}
                >
                  {'Reset to Default'}
                </CButton>
                <CButton
                  type="button"
                  className="mb-3 mx-1"
                  color={'info'}
                  onClick={() => setVisible(!visible)}
                >
                  {'Multiplier'}
                </CButton>
                <CButton
                  type="button"
                  className="mb-3 mx-1"
                  color={'dark'}
                  onClick={handleCopyVisible}
                >
                  {'Copy'}
                </CButton>

                <CLoadingButton
                  className="mb-3 mx-1"
                  type="submit"
                  color="success"
                  loading={loader}
                >
                  Save & Confirm
                </CLoadingButton>
              </>
            )}
          </CCol>
        </CRow>
      </CForm>
      <Notify
        setShowConfirm={setShowConfirm}
        showConfirm={showConfirm}
        setHandleNo={setHandleNo}
        handleNo={handleNo}
        handleYes={handleYes}
        setHandleYes={setHandleYes}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        text="Are You Sure Want to Reset Default"
      />
      <MultiplierModal
        visible={visible}
        setVisible={setVisible}
        gradeId={props.gradeId}
        toggleDetails={props.toggleDetails}
      />
      <CopyGradeModal
        copyVisible={copyVisible}
        setCopyVisible={setCopyVisible}
        gradeId={props.gradeId}
        gradeList={gradeList}
        toggleDetails={props.toggleDetails}
      />
    </>
  )
}

export default PointSystem
